import type { MouseEvent } from 'react'
import { trackGoogleAnalyticsEvent } from './google-analytics'

export type PropertyRegisterAccountMode = 'user' | 'agent'

export function normalizeAppPath(pathname: string) {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1)
  }
  return pathname || '/'
}

export function isInternalNavigationHref(href: string) {
  return href.startsWith('/') || href.startsWith('#')
}

function shouldIgnoreNavigationClick(event: MouseEvent<HTMLElement>) {
  return (
    event.defaultPrevented
    || event.button !== 0
    || event.metaKey
    || event.altKey
    || event.ctrlKey
    || event.shiftKey
  )
}

export function openPropertyRegisterModal(accountMode: PropertyRegisterAccountMode = 'user') {
  if (typeof window === 'undefined') return
  trackGoogleAnalyticsEvent('sign_up_intent', {
    event_category: 'conversion',
    method: accountMode === 'agent' ? 'property_agent_modal' : 'property_user_modal',
    page_path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
  })
  window.dispatchEvent(new CustomEvent('dihaw:open-property-register', { detail: { accountMode } }))
}

export function handlePropertyRegisterModalNavigation(
  event: MouseEvent<HTMLElement>,
  accountMode: PropertyRegisterAccountMode = 'user',
  afterOpen?: () => void,
) {
  if (shouldIgnoreNavigationClick(event)) return
  event.preventDefault()
  afterOpen?.()
  openPropertyRegisterModal(accountMode)
}

export function navigateTo(href: string) {
  if (typeof window === 'undefined') return

  if (href.startsWith('#')) {
    const target = document.querySelector(href)
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    return
  }

  const nextUrl = new URL(href, window.location.origin)
  if (nextUrl.origin !== window.location.origin) {
    window.location.href = nextUrl.toString()
    return
  }

  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  const nextPath = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
  if (currentPath !== nextPath) {
    window.history.pushState({}, '', nextPath)
  }

  window.dispatchEvent(new PopStateEvent('popstate'))
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

export function handleInternalNavigation(
  event: MouseEvent<HTMLAnchorElement>,
  href: string,
  afterNavigate?: () => void,
) {
  if (shouldIgnoreNavigationClick(event)) return

  if (!isInternalNavigationHref(href)) {
    return
  }

  event.preventDefault()
  afterNavigate?.()
  navigateTo(href)
}

export function handleDelegatedInternalNavigation(event: MouseEvent<HTMLElement>) {
  if (shouldIgnoreNavigationClick(event)) return

  const target = event.target instanceof Element ? event.target : null
  const anchor = target?.closest<HTMLAnchorElement>('a[href]') ?? null
  if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) {
    return
  }

  const href = anchor.getAttribute('href') || ''
  if (!isInternalNavigationHref(href)) {
    return
  }

  event.preventDefault()
  navigateTo(href)
}
